import React from 'react';
import { NoLineItemsImg } from '../../features/strategy/no-line-items-img';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { trpc } from '@/utils/trpc-client';
import {
  Control,
  FormProvider as Form,
  Controller,
  FieldPath,
  FieldPathByValue,
  useForm,
  useFormContext
} from 'react-hook-form';
import { StrategyApiUpdateRequest } from 'shared/src/api/strategy-api-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { addYears, format } from 'date-fns';
import { Button } from '@/components/display/button';
import { UTCDate } from '@date-fns/utc';
import { toISODateString } from 'shared/src/date-string-utils/date-utils';
import {
  calculatedProposalValues,
  DateField,
  formatDateSafe,
  formatPctSafe,
  PriceInput
} from '@/features/proposals/strategy-form-utils';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import clsx from 'clsx';

export function CreateProposalStrategy() {
  const { campaign } = useCampaignContext();
  const { mutateAsync, isPending } = trpc.updateProposal.useMutation();
  const utils = trpc.useUtils();

  const form = useForm<StrategyApiUpdateRequest>({
    resolver: zodResolver(StrategyApiUpdateRequest),
    defaultValues: {
      price: campaign.proposed_budget ?? 0,
      referral_commission_rate: campaign.referral_partner_comm || 0,
      agency_markup_rate: campaign.agency_gross_markup || 0,
      start_date: campaign.flight_date ? format(campaign.flight_date, 'yyyy-MM-dd') : undefined,
      end_date: campaign.end_date ? format(campaign.end_date, 'yyyy-MM-dd') : undefined
    },
    mode: 'all'
  });

  async function submitChanges(data: StrategyApiUpdateRequest) {
    if (!isPending) {
      await mutateAsync({
        proposal_id: campaign.proposal_id,
        price: data.price,
        referral_commission_rate: (data.referral_commission_rate || 0) / 100,
        agency_markup_rate: (data.agency_markup_rate || 0) / 100,
        start_date: data.start_date,
        end_date: data.end_date
      });
      await utils.getProposal.invalidate();
      await utils.proposalDiff.invalidate();
    }
  }

  const values = form.watch();
  const { numDays } = calculatedProposalValues(values);

  const { control, formState, handleSubmit } = form;

  return (
    <div className="flex h-full flex-col justify-around">
      <div className="flex flex-col items-center">
        <div className="text-lg font-semibold text-[#3F9FFD]">
          Let’s get going on your strategy!
        </div>
        <div className="text-gray-600">
          We’ll start with the proposed price and dates and work from there.
        </div>
        <div className="my-8">
          <NoLineItemsImg />
        </div>

        <Form {...form}>
          <form
            noValidate
            onSubmit={handleSubmit(submitChanges)}
            className="flex flex-col items-center">
            <div className="mb-6 flex gap-4">
              <InputWrapper
                name="price"
                label="Campaign Price"
                footerLabel="Zoho Proposed Budget"
                footerValue={formatMoney(campaign.proposed_budget)}>
                <PriceInput control={control} type="standalone" />
              </InputWrapper>
              <InputWrapper
                name="referral_commission_rate"
                label="Referral Rate"
                footerLabel="Zoho Referral Comm"
                footerValue={formatPctSafe((campaign.referral_partner_comm || 0) / 100)}>
                <StrategyPctInput name="referral_commission_rate" control={control} />
              </InputWrapper>
              <InputWrapper
                name="agency_markup_rate"
                label="Agency Markup Rate"
                footerLabel="Zoho Agency Gross Markup"
                footerValue={formatPctSafe((campaign.agency_gross_markup || 0) / 100)}>
                <StrategyPctInput name="agency_markup_rate" control={control} />
              </InputWrapper>
              <InputWrapper
                name="start_date"
                label="Campaign Start"
                footerLabel="Zoho Start Date"
                footerValue={formatDateSafe(
                  campaign.flight_date ? toISODateString(campaign.flight_date) : null
                )}>
                <DateField
                  min={new UTCDate('2020-01-01')}
                  max={values.end_date ? new UTCDate(values.end_date) : addYears(new UTCDate(), 10)}
                  name="start_date"
                  type="standalone"
                />
              </InputWrapper>
              <InputWrapper
                name="end_date"
                label="Campaign End"
                footerLabel="Zoho End Date"
                footerValue={formatDateSafe(
                  campaign.end_date ? toISODateString(campaign.end_date) : null
                )}>
                <DateField
                  min={
                    values.start_date ? new UTCDate(values.start_date) : new UTCDate('2020-01-01')
                  }
                  max={addYears(new UTCDate(), 10)}
                  name="end_date"
                  type="standalone"
                />
              </InputWrapper>

              <Duration numDays={numDays} />
            </div>

            <Button type="submit" disabled={formState.isSubmitting}>
              Create strategy
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}

type InputWrapperProps = {
  name: FieldPath<StrategyApiUpdateRequest>;
  label: string;
  footerLabel: string;
  footerValue: string;
  children: React.ReactNode;
};

export function InputWrapper({
  name,
  label,
  footerLabel,
  footerValue,
  children
}: InputWrapperProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="whitespace-nowrap text-sm text-gray-600">
        <label htmlFor={name}>{label}</label>
      </div>
      {children}
      <div className="text-xs text-gray-500">
        {footerLabel}:
        <br />
        <span className="font-semibold">{footerValue}</span>
      </div>
    </div>
  );
}

function Duration({ numDays }: { numDays: string }) {
  return (
    <div className="flex flex-col gap-2 text-sm text-gray-600">
      <div className="text-sm text-gray-600">Duration</div>
      <div className="flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1.5">
        <div className="text-sm font-semibold">{numDays}</div>
        <div className="text-xs text-gray-600">Days</div>
      </div>
      <div className="text-xs text-gray-500">
        Days | Zoho:
        <br />
        <span className="font-semibold">{numDays} Days</span>
      </div>
    </div>
  );
}

type PctInputProps = {
  name: FieldPathByValue<StrategyApiUpdateRequest, number | null | undefined>;
  control: Control<StrategyApiUpdateRequest>;
};

function StrategyPctInput({ name, control }: PctInputProps) {
  const { formState } = useFormContext<StrategyApiUpdateRequest>();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <div className="relative w-full">
          <input
            className={clsx(
              'w-full rounded-sm px-4 py-1.5 pr-8 text-right text-sm font-normal text-blue-600 focus:ring-0',
              formState.errors[name]
                ? 'border border-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-gray-300'
            )}
            type="number"
            step="1"
            min="0"
            max="50"
            value={value}
            onChange={e => {
              const parsed = parseFloat(e.target.value);
              onChange(isNaN(parsed) ? 0 : parsed);
            }}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">%</span>
        </div>
      )}
    />
  );
}
